import React, { useState, useCallback, useEffect } from 'react'
import { useFormik } from 'formik'
import { useDispatch } from 'react-redux'
import Modal from '@material-ui/core/Modal'
import CloseIcon from '@material-ui/icons/Close'
import IconButton from '@material-ui/core/IconButton'
import CircularProgress from '@material-ui/core/CircularProgress'
import * as yup from 'yup'

import Title from 'components/CommonUI/Title/Title'
import { modalTypesConstant } from '../DevicesManagement'
import TextInput from 'components/CommonUI/TextInput/TextInput'
import Button from 'components/CommonUI/Button/Button'
import { devicesIconComponent } from 'constants/deviceTypeConstants'
import {
  deleteDevices,
  updateDevices,
  createDevices,
  getUpdatesStateSelecor,
  clearUpdatesState,
} from 'redux/reducers/devices'
import { useShallowEqualSelector } from 'custom-hooks'
import { hex } from 'utils/hex'

const COLORS = [
  '#1772F5',
  '#9A8B03',
  '#D715A1',
  '#0F9F03',
  '#E5911C',
  '#3766BB',
  '#444C5A',
  '#269DC5',
]

const validErrors = {
  icon: 'You should select icon',
  color: 'You should select color',
}

let schema = yup.object().shape({
  deviceName: yup
    .string('Device name should be string')
    .trim("Device name can't start or end the space character")
    .min(2, 'Device name must be at least 2 characters')
    .max(20, 'Device name must be at most 20 characters')
    .required('Device name is a required field'),
})

const DeviceModal = ({ handleClose, type, editingData = {} }) => {
  const dispatch = useDispatch()

  const { isFetchingUpdates, isSuccessUpdates } = useShallowEqualSelector(
    getUpdatesStateSelecor
  )

  const [selectedIcon, setSelectedIcon] = useState(() => {
    return type === modalTypesConstant.edit ? editingData?.icon : null
  })
  const [selectedColor, setSelectedColor] = useState(() => {
    return type === modalTypesConstant.edit ? editingData?.color : null
  })

  const [isSubmitingFailed, setSubmitingFailed] = useState(false)

  const modalTitle =
    type === modalTypesConstant.create
      ? 'Create a new device'
      : `${editingData?.name} editing`

  const formik = useFormik({
    initialValues: {
      deviceName: type === modalTypesConstant.edit ? editingData?.name : '',
    },
    onSubmit: (values) => {
      if (selectedColor && selectedIcon) {
        if (type === modalTypesConstant.edit) {
          dispatch(
            updateDevices({
              id: editingData.id,
              color: selectedColor,
              icon: selectedIcon,
              name: String(values.deviceName).trim(),
            })
          )
        } else {
          dispatch(
            createDevices({
              color: selectedColor,
              icon: selectedIcon,
              name: String(values.deviceName).trim(),
            })
          )
        }
      } else {
        setSubmitingFailed(true)
      }
    },
    validationSchema: schema,
    validateOnBlur: true,
    validateOnChange: true,
  })

  const handleDeviceIconClick = (icon) => () => {
    setSelectedIcon(icon)
  }

  const handleDeviceColorClick = (color) => () => {
    setSelectedColor(color)
  }

  const handleDelete = useCallback(() => {
    dispatch(deleteDevices(editingData?.id))
  }, [dispatch, editingData?.id])

  useEffect(() => {
    if (isSuccessUpdates) {
      dispatch(clearUpdatesState())
      handleClose()
    }
  }, [isSuccessUpdates, handleClose, dispatch])

  return (
    <Modal
      open={true}
      className="device_modal_container"
      closeAfterTransition={true}
    >
      <div className="device_modal_body">
        <div className="modal_header">
          <div className="title_wrap">
            <Title title={modalTitle} component="h3" className="modal_title" />
            {isFetchingUpdates && (
              <CircularProgress style={{ color: '#151F31' }} size={30} />
            )}
          </div>
          <IconButton onClick={handleClose} className="modal_close_button">
            <CloseIcon className="modal_close_button_icon" />
          </IconButton>
        </div>
        <form className="modal_main" onSubmit={formik.handleSubmit}>
          <TextInput
            label="Device name"
            classNameContainer="device_name_input_container"
            placeholder="Enter device name"
            onChange={formik.handleChange}
            value={formik.values.deviceName}
            name={'deviceName'}
            errorMessage={formik.touched.deviceName && formik.errors.deviceName}
            onBlur={formik.handleBlur}
          />
          <div className="device_type_icon_container">
            <span className="device_type_icon_label">Choose device icon</span>
            <div className="device_type_icon_wrap">
              {Object.entries(devicesIconComponent).map(
                ([iconName, { ComponentIcon }]) => {
                  const activeClassName =
                    selectedIcon === iconName ? 'active' : ''
                  return (
                    <span
                      className={`icon_item_container ${activeClassName}`}
                      key={iconName}
                      onClick={handleDeviceIconClick(iconName)}
                      style={{
                        background:
                          selectedIcon === iconName
                            ? selectedColor
                              ? hex(selectedColor)
                              : '#eaebed'
                            : 'unset',
                      }}
                    >
                      <ComponentIcon
                        color={
                          selectedIcon === iconName && selectedColor
                            ? selectedColor
                            : '#151F31'
                        }
                      />
                    </span>
                  )
                }
              )}
            </div>
            {!selectedIcon && (isSubmitingFailed || !!formik.submitCount) && (
              <span className="valid_error_message">{validErrors.icon}</span>
            )}
          </div>
          <div className="device_type_icon_container">
            <span className="device_type_icon_label">Choose device color</span>
            <div className="device_type_icon_wrap device_color_container">
              {COLORS.map((color) => (
                <span
                  className={`device_type_color ${
                    selectedColor === color ? 'active' : ''
                  }`}
                  key={color}
                  style={{ backgroundColor: `${color}` }}
                  onClick={handleDeviceColorClick(color)}
                />
              ))}
            </div>
            {!selectedColor && (isSubmitingFailed || !!formik.submitCount) && (
              <span className="valid_error_message">{validErrors.color}</span>
            )}
          </div>
          {type === modalTypesConstant.create ? (
            <Button
              fetchingIndicator={isFetchingUpdates}
              disabled={isFetchingUpdates}
              type="subbmit"
            >
              Create the device
            </Button>
          ) : (
            <div className="modal_buttons_container">
              <Button
                className="modal_button save_button"
                disabled={isFetchingUpdates}
                type="subbmit"
              >
                Save
              </Button>
              <Button
                className="modal_button delete_button"
                variant="outlined"
                color="secondary"
                onClick={handleDelete}
                disabled={isFetchingUpdates}
                type="button"
              >
                Delete device
              </Button>
            </div>
          )}
        </form>
      </div>
    </Modal>
  )
}

export default DeviceModal
